import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import { FC, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsRTL } from '@context/SessionSettingsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body2 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'
import { CircleCloseIcon } from '@ui/icons/CircleClose.icon'
import { KebabIcon } from '@ui/icons/Kebab.icon'
import { ReplayIcon } from '@ui/icons/Replay.icon'
import {
  SensitiveTableCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'
import { TableHeaderTooltip } from '@ui/v2/Table/TableHeaderTooltip'

import { useWindowResize } from '@utils/domUtils'
import { placeholder } from '@utils/placeholder'

import { NameDto } from '@model/NameDto'
import {
  IBRebateApprovalSortKeysDto,
  IntroducingBrokerRebateApprovalDto,
  RebateApprovalStatus,
  rebateApprovalEditableStatuses,
} from '@model/RebateApprovalsDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'
import { APPROVAL_STATUS_STYLE, StatusIcon } from '@shared/components/StatusIcon/StatusIcon'

import { defaultApprovalsSort } from './hooks/useIBRebateApprovals'

import styles from './ApprovalsPageTable.module.scss'

interface ApprovalsPageTableProps {
  sortOptions?: NameDto<IBRebateApprovalSortKeysDto | null>[]
  sticky?: 'left'
  selectedIds?: string[]
  onSelect: (ids: string[]) => void
  onReject: (group: IntroducingBrokerRebateApprovalDto) => void
  onRecalculate: (group: IntroducingBrokerRebateApprovalDto) => void
  onApprove: (group: IntroducingBrokerRebateApprovalDto) => void
  tableQuery: PaginatedQueryResult<Partial<IntroducingBrokerRebateApprovalDto>>
  isAnyFilterActive: boolean
  topContent?: ReactNode
  isRebatesManagement?: boolean
}

export const ApprovalsPageTable: FC<ApprovalsPageTableProps> = (props) => {
  const {
    tableQuery,
    selectedIds = [],
    sortOptions,
    isAnyFilterActive,
    sticky,
    topContent,
    isRebatesManagement,
  } = props
  const { pageQuery, setPageQuery, isLoading, data: rebateApprovals } = tableQuery
  const { onReject, onRecalculate, onSelect, onApprove } = props

  const { formatNumber, formatMoney } = useFormatNumber()
  const isRTL = useIsRTL()
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const hasRowActions = rebateApprovals.some(
    ({ status }) =>
      status?.id === RebateApprovalStatus.New || status?.id === RebateApprovalStatus.Failed
  )

  const handleSelect = (id: string, selected: boolean) => {
    if (!selected) {
      onSelect(selectedIds?.filter((activeId) => activeId !== id))

      return
    }

    onSelect([...selectedIds, id])
  }

  const selectableIds = rebateApprovals
    .filter((approval) =>
      rebateApprovalEditableStatuses.includes(approval.status?.id as RebateApprovalStatus)
    )
    .map((approval) => approval.id)
    .filter(Boolean) as string[]
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id))
  const someSelected = !allSelected && selectableIds.some((id) => selectedIds.includes(id))

  const handleSelectAll = () => {
    onSelect(allSelected ? [] : selectableIds)
  }
  const getColumnAlign = (id?: IBRebateApprovalSortKeysDto) => {
    switch (id) {
      case 'tradingAccountName':
      case 'accountType':
      case 'rebatePayoutStatusId':
      case 'groupName':
        return isRTL ? 'right' : 'left'

      default:
        return isRTL ? 'left' : 'right'
    }
  }

  return (
    <div className={isRebatesManagement && !selectedIds.length ? styles.widgetTableWrapper : ''}>
      <Table
        isLoading={isLoading}
        noResults={
          !rebateApprovals.length && (
            <EmptyApprovalsPageTable isAnyFilterActive={isAnyFilterActive} />
          )
        }
        sticky={sticky}
        topContent={topContent}
        size={isRebatesManagement ? 'sm' : 'md'}
        hideScrollbarOnMobile
      >
        <TableHeader selectedIsActive={selectedIds.length > 0}>
          <TableRow>
            {sortOptions?.map((option) =>
              option.id ? (
                <TableHeadSort
                  id={option.id}
                  key={option.id}
                  sort={pageQuery?.sort || defaultApprovalsSort}
                  sortOrder={pageQuery?.sortOrder}
                  onSelectAll={option.id === 'tradingAccountName' ? handleSelectAll : undefined}
                  allSelected={option.id === 'tradingAccountName' ? allSelected : undefined}
                  someSelected={option.id === 'tradingAccountName' ? someSelected : undefined}
                  selectDisabled={!selectableIds.length}
                  align={getColumnAlign(option.id)}
                  setSort={(sort, sortOrder) => {
                    setPageQuery?.({
                      ...pageQuery,
                      sort,
                      sortOrder,
                    })
                  }}
                >
                  {option.name}
                  <TableHeaderTooltip<Partial<Record<IBRebateApprovalSortKeysDto, string>>>
                    headerIds={{
                      accumulatedVolumeFxm: t('IB.RebatesPage.Forex & Metals'),
                      accumulatedVolumeOther: t('IB.RebatesPage.Other instrument groups'),
                      accumulatedCommission: t('IB.RebatesPage.Commission accrued for TA'),
                    }}
                    targetHeaderId={option.id}
                  />
                </TableHeadSort>
              ) : (
                <TableHead key={option.name}>{option.name}</TableHead>
              )
            )}
            {hasRowActions && <TableHead />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rebateApprovals.map((approval) => {
            const selected = selectedIds?.includes(approval?.id as string)
            const selectable = rebateApprovalEditableStatuses.includes(
              approval.status?.id as RebateApprovalStatus
            )
            const statusCell = approval.status ? (
              <SensitiveTableCell align='left'>
                <Box flex align='center' gap='x8'>
                  <StatusIcon styles={APPROVAL_STATUS_STYLE} status={approval.status.id} />
                  <Box>
                    <SensitiveData>
                      <Body2 color='text-high-em' medium>
                        {approval.status.name}
                      </Body2>
                    </SensitiveData>
                  </Box>
                </Box>
              </SensitiveTableCell>
            ) : null
            return (
              <TableRow key={approval.id} selected={selected}>
                {approval.tradingAccount ? (
                  <SensitiveTableCell
                    onSelect={() => handleSelect(approval.id as string, !selected)}
                    disabled={!selectable}
                    checkboxTooltipText={
                      !selectable
                        ? t('IB.RebatesPage.ApprovalsPage.Cannot update finalized rebates')
                        : undefined
                    }
                    alert={approval.showNotification}
                    selected={selected}
                    align='left'
                  >
                    {approval.tradingAccount?.name}
                  </SensitiveTableCell>
                ) : null}
                {isRebatesManagement && statusCell}
                {approval.tradingAccountType ? (
                  <SensitiveTableCell align='left'>
                    {approval.tradingAccountType.name}
                  </SensitiveTableCell>
                ) : null}
                {sortOptions?.some((option) => option.id === 'groupName') ? (
                  <SensitiveTableCell align='left'>
                    {placeholder.NA(t, approval.rebateGroup?.name)}
                  </SensitiveTableCell>
                ) : null}
                {typeof approval.accumulatedVolumeFxm === 'number' ? (
                  <SensitiveTableCell align='right'>
                    <Tooltip
                      content={formatMoney(approval.accumulatedVolumeFxm)}
                      disabled={approval.accumulatedVolumeFxm < 100_000_000}
                    >
                      {formatMoney(approval.accumulatedVolumeFxm)}
                    </Tooltip>
                  </SensitiveTableCell>
                ) : null}
                {typeof approval.accumulatedVolumeOther === 'number' ? (
                  <SensitiveTableCell align='right'>
                    <Tooltip
                      content={formatMoney(approval.accumulatedVolumeOther)}
                      disabled={approval.accumulatedVolumeOther < 100_000_000}
                    >
                      {formatMoney(approval.accumulatedVolumeOther)}
                    </Tooltip>
                  </SensitiveTableCell>
                ) : null}
                {typeof approval.accumulatedCommission === 'number' ? (
                  <SensitiveTableCell align='right'>
                    {formatMoney(approval.accumulatedCommission)}
                  </SensitiveTableCell>
                ) : null}
                {typeof approval.rebateAmount === 'number' ? (
                  <SensitiveTableCell align='right'>
                    {formatNumber(approval.rebateAmount, { maxDecimalPlaces: 10 })}
                  </SensitiveTableCell>
                ) : null}
                {!isRebatesManagement && statusCell}
                {approval.status
                  ? (approval.status.id === RebateApprovalStatus.New ||
                      approval.status.id === RebateApprovalStatus.Failed) && (
                      <TableCell
                        hasTruncate={false}
                        align='left'
                        className={styles.rowActions}
                        width={isRebatesManagement && !isMobile ? '' : 50}
                      >
                        {isRebatesManagement && !isMobile ? (
                          <Box flex gap='x8'>
                            <IconButton.Secondary
                              size='sm'
                              icon={CircleCheckIcon}
                              onClick={() =>
                                onApprove(approval as IntroducingBrokerRebateApprovalDto)
                              }
                            />

                            <IconButton.Secondary
                              size='sm'
                              icon={ReplayIcon}
                              onClick={() =>
                                onRecalculate(approval as IntroducingBrokerRebateApprovalDto)
                              }
                            />
                            <IconButton.Secondary
                              size='sm'
                              icon={CircleCloseIcon}
                              onClick={() =>
                                onReject(approval as IntroducingBrokerRebateApprovalDto)
                              }
                            />
                          </Box>
                        ) : (
                          <RowActions
                            status={approval.status.id}
                            onRecalculate={() =>
                              onRecalculate(approval as IntroducingBrokerRebateApprovalDto)
                            }
                            onReject={() =>
                              onReject(approval as IntroducingBrokerRebateApprovalDto)
                            }
                            onApprove={() =>
                              onApprove(approval as IntroducingBrokerRebateApprovalDto)
                            }
                          />
                        )}
                      </TableCell>
                    )
                  : null}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

interface RowActionsProps {
  onApprove: () => void
  onReject: () => void
  onRecalculate: () => void
  status: RebateApprovalStatus
}

export const RowActions: FC<RowActionsProps> = (props) => {
  const { onApprove, onReject, onRecalculate, status } = props

  const { t } = useTranslation()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const allowChangeStatus =
    status === RebateApprovalStatus.New || status === RebateApprovalStatus.Failed

  if (!allowChangeStatus) {
    return null
  }

  const actions = [
    {
      label: t('IB.RebatesPage.ApprovalsPage.Approve'),
      onClick: () => {
        onApprove()
        setIsDropdownOpen(false)
      },
      icon: CircleCheckIcon,
    },
    {
      label: t('IB.RebatesPage.ApprovalsPage.Reject'),
      onClick: () => {
        onReject()
        setIsDropdownOpen(false)
      },
      icon: CircleCloseIcon,
    },
    {
      label: t('IB.RebatesPage.ApprovalsPage.Re-calculate'),
      onClick: () => {
        onRecalculate()
        setIsDropdownOpen(false)
      },
      icon: ReplayIcon,
    },
  ]

  return (
    <Popover
      direction='topRight'
      isOpen={isDropdownOpen}
      setIsOpen={setIsDropdownOpen}
      content={
        <PopoverCard>
          <PopoverList>
            {actions?.map((action) => (
              <ListItem
                key={action.label}
                onClick={action.onClick}
                startSlot={{ icon: action.icon }}
              >
                {action.label}
              </ListItem>
            ))}
          </PopoverList>
        </PopoverCard>
      }
    >
      <IconButton.Ghost icon={KebabIcon} />
    </Popover>
  )
}

interface EmptyGroupsPageTableProps {
  isAnyFilterActive?: boolean
}

export const EmptyApprovalsPageTable: FC<EmptyGroupsPageTableProps> = (props) => {
  const { isAnyFilterActive } = props

  const { t } = useTranslation()

  return (
    <TableNoResults
      title={t('IB.RebatesPage.GroupsPage.Nothing to show')}
      content={isAnyFilterActive && t('IB.RebatesPage.GroupsPage.Please, adjust your filters')}
    />
  )
}
